/**
 * Calendar-date helpers, identical to the current app. Dates are local-time midnights;
 * stored dates are "YYYY-MM-DD" strings.
 */

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Date → "YYYY-MM-DD" (local time). */
export function toISODate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** "YYYY-MM-DD" → local midnight, or null when missing or malformed. */
export function parseISODate(s: unknown): Date | null {
  if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Local midnight of the given moment (default: now). */
export function startOfDay(now: Date = new Date()): Date {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Adds calendar months; the 31st becomes the last day of a shorter month. */
export function addMonths(d: Date, n: number): Date {
  const y = d.getFullYear(), m = d.getMonth() + n, day = d.getDate();
  const last = new Date(y, m + 1, 0).getDate();
  return new Date(y, m, Math.min(day, last));
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Whole days from b to a (rounded, so daylight-saving shifts don't matter). */
export function dayDiff(a: Date, b: Date): number {
  return Math.round((a.getTime() - b.getTime()) / 864e5);
}
